import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// Import i18n setup
import i18n from 'i18next';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files - Only supported languages
import enTranslations from './i18n/en.json';
import esTranslations from './i18n/es.json';
import frTranslations from './i18n/fr.json';
import svTranslations from './i18n/sv.json';
import { componentLogger } from '@/lib/logging';
import { loadTranslationOverrides, mergeTranslationOverrides } from '@/lib/translation-loader';

// Setup global error handler to catch all uncaught errors
import { setupGlobalErrorHandler } from '@/lib/global-error-handler';
setupGlobalErrorHandler();

// Initialize i18n
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enTranslations, // 'common' namespace
      },
      es: {
        common: esTranslations, // 'common' namespace
      },
      fr: {
        common: frTranslations, // 'common' namespace
      },
      sv: {
        common: svTranslations, // 'common' namespace
      },
    },
    fallbackLng: 'en', // Fallback language if detected lng is not available
    defaultNS: 'common',
    // Enable debug output in development OR if a specific query param is set for staging
    debug: import.meta.env['DEV'] || window.location.search.includes('i18next_debug=true'),
    interpolation: {
      escapeValue: false, // React already safes from xss
      prefix: '{',
      suffix: '}',
    },
    detection: {
      // Order and from where user language should be detected
      // Cookie is checked BEFORE localStorage to persist across logout (localStorage cleared by signOut)
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      // Keys or params to lookup language from
      lookupQuerystring: 'lng',
      lookupCookie: 'i18nextLng',
      lookupLocalStorage: 'i18nextLng',
      // Cache user language on both cookie and localStorage
      caches: ['localStorage', 'cookie'],
    },
    react: {
      // Wrap text nodes with span elements to protect against Google Translate DOM manipulation
      // This helps prevent React crashes when browser translation is active
      // See: https://github.com/facebook/react/issues/11538
      transWrapTextNodes: 'span',
    },
  });

// Expose i18n for debugging purposes
if (import.meta.env['DEV'] || window.location.search.includes('i18next_debug=true')) {
  (window as { i18n?: typeof i18n }).i18n = i18n;
}

// Load and apply translation overrides BEFORE app render
// CRITICAL FIX: This must complete BEFORE React renders to prevent race conditions
// where components render with static translations before overrides are applied
async function loadAndApplyTranslationOverrides() {
  try {
    // Get current language and normalize it (e.g., "en-US" → "en")
    // This ensures consistency with how overrides are stored in database and cache
    const detectedLanguage = i18n.language || 'en';
    const parts = detectedLanguage.split('-');
    const currentLanguage = parts[0] || 'en';

    componentLogger.debug('i18n', `Loading translation overrides for ${currentLanguage} (detected: ${detectedLanguage})`);

    // Load overrides (from cache or database)
    const overrides = await loadTranslationOverrides(currentLanguage);

    // CRITICAL DEBUG: Log override count and sample data to diagnose Test 4 failure
    const overrideCount = Object.keys(overrides).length;
    const firstKey = Object.keys(overrides)[0];
    componentLogger.debug('i18n', `Translation overrides loaded: ${overrideCount} keys`, {
      currentLanguage,
      overrideKeys: Object.keys(overrides).slice(0, 5), // First 5 keys for debugging
      sampleKey: firstKey || 'NO_KEYS',
      sampleValue: firstKey ? overrides[firstKey] : 'NO_VALUE'
    });

    if (overrideCount === 0) {
      componentLogger.warn('i18n', 'No translation overrides found - using static translations only', {
        currentLanguage,
        diagnostics: {
          checkDatabase: 'SELECT * FROM translation_overrides WHERE language_code = \'' + currentLanguage + '\' AND is_active = true',
          checkCache: 'localStorage.getItem(\'translation_overrides_' + currentLanguage + '_v1\')',
          checkEdgeFunction: 'Network tab → get-translations response should have data.count > 0'
        }
      });
      return;
    }

    componentLogger.debug('i18n', `Applying ${overrideCount} translation overrides for ${currentLanguage}`);

    // Get static translations for current language
    const staticTranslations = i18n.getResourceBundle(currentLanguage, 'common') as Record<string, unknown>;

    // Merge overrides with static translations
    const mergedTranslations = mergeTranslationOverrides(staticTranslations, overrides);

    // Update i18n resources
    i18n.addResourceBundle(currentLanguage, 'common', mergedTranslations, true, true);

    // CRITICAL DEBUG: Verify merge worked correctly by checking a sample key
    const testKey = Object.keys(overrides)[0];
    if (testKey) {
      const mergedValue = i18n.t(testKey);
      const expectedValue = overrides[testKey];
      componentLogger.debug('i18n', 'Translation merge verification', {
        testKey,
        expectedValue,
        actualValue: mergedValue,
        matchesExpected: mergedValue === expectedValue,
        success: mergedValue === expectedValue ? '✅ Merge successful' : '❌ Merge failed'
      });
    }

    componentLogger.debug('i18n', 'Translation overrides applied successfully');
  } catch (error) {
    componentLogger.error('i18n', 'Failed to load translation overrides', error as Error);
    // Continue with static translations if override loading fails
  }
}

// Initialize app immediately with static translations
// Load translation overrides asynchronously AFTER app renders
// CRITICAL FIX: This prevents 3-minute blank screen caused by blocking on translation load
// before auth context initializes (which translation loading depends on)
function initializeApp() {
  // STEP 1: Render app IMMEDIATELY with static translations
  const rootElement = document.getElementById('root');
  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </React.StrictMode>
    );
    componentLogger.debug('i18n', 'App rendered with static translations');
  } else {
    componentLogger.error('i18n', "Failed to find the root element with ID 'root'");
    return;
  }

  // STEP 2: Load translation overrides asynchronously (non-blocking)
  // This happens AFTER app renders and auth context initializes
  loadAndApplyTranslationOverrides()
    .then(() => {
      componentLogger.debug('i18n', 'Translation overrides applied post-render');
      // Trigger i18n update event to refresh components with overrides
      i18n.emit('languageChanged', i18n.language);
    })
    .catch((error) => {
      componentLogger.error('i18n', 'Post-render translation override loading failed', error as Error);
      // App continues with static translations - no user impact
    });
}

// Start app initialization (non-blocking - renders immediately)
initializeApp();

// Handle chunk loading failures from new deployments
// When a new version is deployed, old chunk files no longer exist
// This automatically reloads the page to fetch new chunks
window.addEventListener('vite:preloadError', () => {
  componentLogger.warn('i18n', 'Chunk load failure detected, reloading page to fetch new version');
  window.location.reload();
});